import {
  Asset,
  AssetAppeal,
  AssetAppealRequestActionStatusEnum,
  Report,
  Roles,
} from "types";
import ReportList from "./RespondentAppealReportList";
import { useAuth } from "context/AuthContext";
import { errorHandler, hasRole } from "utils/helper";
import { useState, useEffect } from "react";
import FileViewer from "components/file-components/fileViewer2";
import { getRespondentAssetAppealReportss } from "services/assetAppealService";
import { useTooltip } from "context/TooltipProvider";
import { RespondentAppealReportTabs } from "./RespondentAppealAddReport";

export default function RespondentAppealReportContainer({
  appealId,
  appeal,
  setActiveTab,
  setAppeal,
}: {
  appealId: string;
  appeal?: AssetAppeal;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
}) {
  const { loginData } = useAuth();
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);

  const [reports, setReports] = useState<Report[]>([]);
  const [recoveryReports, setRecoveryReports] = useState<Report[]>([]);

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  const { showTooltip } = useTooltip();

  useEffect(() => {
    getRespondentAssetAppealReportss(appealId)
      .then((d) => {
        setReports(d);
        showTooltip("Reports successfully fetched", "success");
      })
      .catch((e) => {
        showTooltip(errorHandler(e), "error");
        console.error("Failed to fetch");
      });
  }, [appealId]);

  const showReportTabStatuses = [
    AssetAppealRequestActionStatusEnum?.APPEAL_APPROVED,
  ];

  const handleReportDataUpdated = async () => {
    const [proh] = await Promise.all([
      getRespondentAssetAppealReportss(appealId),
    ]);
    setReports(proh);
    console.log({ len: reports.length });
  };

  // Check if all assets have final reports
  const hasFinalReportForAllAssets = () => {
    if (!appeal?.asset || !Array.isArray(appeal.asset)) return false;

    const assets = appeal.asset;
    const finalReports = reports.filter(
      (report) => report.is_next_appointment_reason_final_verdict
    );

    // Get all asset IDs that have final reports
    const assetsWithFinalReports = new Set();
    finalReports.forEach((report) => {
      if (report.asset) {
        if (Array.isArray(report.asset)) {
          report.asset.forEach((asset) => {
            if (asset?.id) assetsWithFinalReports.add(asset.id);
          });
        } else if (report.asset?.id) {
          assetsWithFinalReports.add(report.asset.id);
        }
      }
    });

    // Check if every asset has a final report
    return assets.every((asset) => assetsWithFinalReports.has(asset.id));
  };

  // Get assets that don't have final reports yet
  const getAssetsNeedingFinalReport = (): Asset[] => {
    if (!appeal?.asset || !Array.isArray(appeal.asset)) return [];

    const assets = appeal.asset;
    const finalReports = reports.filter(
      (report) => report.is_next_appointment_reason_final_verdict
    );

    // Get all asset IDs that have final reports
    const assetsWithFinalReports = new Set();
    finalReports.forEach((report) => {
      if (report.asset) {
        if (Array.isArray(report.asset)) {
          report.asset.forEach((asset) => {
            if (asset?.id) assetsWithFinalReports.add(asset.id);
          });
        } else if (report.asset?.id) {
          assetsWithFinalReports.add(report.asset.id);
        }
      }
    });

    // Return assets that don't have final reports
    return assets.filter((asset) => !assetsWithFinalReports.has(asset.id));
  };

  const shouldShowReportTabs =
    isAssetProsecutor &&
    showReportTabStatuses.includes(
      appeal?.action_status as AssetAppealRequestActionStatusEnum
    ) &&
    !hasFinalReportForAllAssets();

  return (
    <div className="flex gap-3">
      <ReportList
        appealId={appealId}
        fileRowProps={fileRowProps}
        reports={reports}
        recoveryReports={recoveryReports}
        appeal={appeal}
      />

      {shouldShowReportTabs && (
        <RespondentAppealReportTabs
          appealId={appealId}
          assets={appeal?.asset as Asset[]}
          assetsNeedingReport={getAssetsNeedingFinalReport()}
          setActiveTab={setActiveTab}
          onNewDataAdded={handleReportDataUpdated}
          appeal={appeal as AssetAppeal}
          setAppeal={setAppeal}
          reports={reports}
        />
      )}

      <FileViewer
        selectedApplicantDocument={selectedDocument ?? undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}
